import { useState, useRef, useCallback } from 'react';
import { chatLogger } from '@/utils/logger';
import { initializeConversation, saveUserResponse, updateConversationContext, markConversationCompleted, updateConversationPiiToken } from '@/services/chatDbService';
import { Question } from '@/types/theme';

/**
 * Hook for managing conversation state
 * Extracted from useUnifiedChat for better separation of concerns
 */

export type ConversationState = 'initial' | 'welcome_shown' | 'in_progress' | 'completed';

export interface ConversationStateManager {
  // State
  conversationState: ConversationState;
  currentQuestionIndex: number;
  flowCompleted: boolean;
  userResponses: Record<string, string>;
  sessionId: { current: string };
  conversationId: { current: string | null };

  // Actions
  setConversationState: (state: ConversationState) => void;
  setCurrentQuestionIndex: (index: number) => void;
  setFlowCompleted: (completed: boolean) => void;
  addUserResponse: (questionId: string, response: string, question?: Question) => void;
  resetConversation: () => void;
  generateNewSession: () => string;
  initializeDbConversation: (chatbotId: string, existingPiiToken?: string) => Promise<void>;
  completeDbConversation: () => Promise<void>;
  getSessionPiiToken: (chatbotId: string) => string | null;
}

export function useConversationState(): ConversationStateManager {
  const [conversationState, setConversationState] = useState<ConversationState>('initial');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [flowCompleted, setFlowCompleted] = useState(false);
  const [userResponses, setUserResponses] = useState<Record<string, string>>({});

  // Generate session ID only once
  const sessionId = useRef(generateSessionId());
  // Track database conversation ID
  const conversationId = useRef<string | null>(null);

  function generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  const addUserResponse = useCallback(async (questionId: string, response: string, question?: Question) => {
    chatLogger.conversation('Adding user response', { questionId, response: response.substring(0, 50) + '...' });

    // Update local state
    setUserResponses(prev => ({
      ...prev,
      [questionId]: response
    }));

    // Save to database if we have a conversation ID and question
    if (conversationId.current && question) {
      try {
        const success = await saveUserResponse(conversationId.current, question, response);
        if (success) {
          chatLogger.conversation('User response saved to database', { questionId, conversationId: conversationId.current });
        } else {
          chatLogger.error('Failed to save user response to database', { questionId, conversationId: conversationId.current });
        }
      } catch (error) {
        chatLogger.error('Error saving user response to database', { error, questionId, conversationId: conversationId.current });
      }
    }
  }, []);

  const initializeDbConversation = useCallback(async (chatbotId: string, existingPiiToken?: string) => {
    if (!chatbotId) {
      chatLogger.error('Cannot initialize conversation: missing chatbotId');
      return;
    }

    try {
      chatLogger.conversation('Initializing database conversation', { chatbotId, sessionId: sessionId.current, existingPiiToken: !!existingPiiToken });

      const conversation = await initializeConversation(chatbotId, sessionId.current);
      if (conversation) {
        conversationId.current = conversation.id;
        chatLogger.conversation('Database conversation initialized', {
          conversationId: conversation.id,
          chatbotId,
          sessionId: sessionId.current
        });

        // Auto-create PII record for this session if no existing token
        if (!existingPiiToken) {
          console.log('Auto-create PII record for this session if no existing token')
          try {
            const { createPiiData } = await import('@/utils/piiUtils');
            const piiResponse = await createPiiData({
              _session_id: sessionId.current,
              _conversation_id: conversation.id,
              _chatbot_id: chatbotId,
              _created_at: new Date().toISOString()
            }, 30);

            chatLogger.conversation('Auto-created PII record for session', {
              sessionId: sessionId.current,
              piiToken: piiResponse.token.slice(0, 8) + '...',
              conversationId: conversation.id
            });

            // Store the PII token in session storage for potential later use
            try {
              sessionStorage.setItem(`pii_token_${chatbotId}_${sessionId.current}`, piiResponse.token);
              await updateConversationPiiToken(conversation.id, piiResponse.token)
            } catch (storageError) {
              chatLogger.error('Failed to store PII token in session storage', { storageError });
            }
          } catch (piiError) {
            chatLogger.error('Failed to auto-create PII record', { piiError, chatbotId, sessionId: sessionId.current });
            // Don't fail the conversation initialization if PII creation fails
          }
        }
      } else {
        chatLogger.error('Failed to initialize database conversation', { chatbotId, sessionId: sessionId.current });
      }
    } catch (error) {
      chatLogger.error('Error initializing database conversation', { error, chatbotId, sessionId: sessionId.current });
    }
  }, []);

  const completeDbConversation = useCallback(async () => {
    if (!conversationId.current) {
      chatLogger.conversation('No conversation to complete');
      return;
    }

    try {
      chatLogger.conversation('Completing database conversation', { conversationId: conversationId.current });

      // Create conversation context from user responses
      const conversationContext = Object.entries(userResponses).map(([questionId, answer]) => ({
        question: questionId,
        answer
      }));

      // Update conversation context
      await updateConversationContext(conversationId.current, conversationContext);

      // Mark conversation as completed
      await markConversationCompleted(conversationId.current, conversationContext);

      chatLogger.conversation('Database conversation completed', { conversationId: conversationId.current });
    } catch (error) {
      chatLogger.error('Error completing database conversation', { error, conversationId: conversationId.current });
    }
  }, [userResponses]);

  const resetConversation = useCallback(() => {
    const resetId = Math.random().toString(36).substring(2, 8);

    chatLogger.conversation(`[${resetId}] Resetting conversation state`, {
      currentState: conversationState,
      currentQuestionIndex,
      flowCompleted,
      userResponsesCount: Object.keys(userResponses).length
    });

    setConversationState('initial');
    setCurrentQuestionIndex(0);
    setFlowCompleted(false);
    setUserResponses({});
    conversationId.current = null; // Reset conversation ID
  }, [conversationState, currentQuestionIndex, flowCompleted, userResponses]);

  const generateNewSession = useCallback((): string => {
    const newSessionId = generateSessionId();
    sessionId.current = newSessionId;
    chatLogger.conversation('Generated new session ID', { sessionId: newSessionId });
    return newSessionId;
  }, []);

  const getSessionPiiToken = useCallback((chatbotId: string): string | null => {
    try {
      const token = sessionStorage.getItem(`pii_token_${chatbotId}_${sessionId.current}`);
      if (token) {
        chatLogger.conversation('Retrieved session PII token', {
          sessionId: sessionId.current,
          tokenPreview: token.slice(0, 8) + '...'
        });
      }
      return token;
    } catch (error) {
      chatLogger.error('Failed to retrieve session PII token', { error, chatbotId, sessionId: sessionId.current });
      return null;
    }
  }, []);

  return {
    conversationState,
    currentQuestionIndex,
    flowCompleted,
    userResponses,
    sessionId,
    conversationId,
    setConversationState,
    setCurrentQuestionIndex,
    setFlowCompleted,
    addUserResponse,
    resetConversation,
    generateNewSession,
    initializeDbConversation,
    completeDbConversation,
    getSessionPiiToken
  };
}
